<template>
  <div class="modal" :class="{ 'is-active': isVisible }">
    <div class="modal-background" @click="close"></div>
    <div class="modal-content" :class="`modal-${size}`">
      <div class="modal-inner-wrapper">
        <a id="modalclose" @click="close"><i class="fas fa-times"></i></a>
        <h1>Comp Details</h1>
        <div v-if="comp">
          <!-- Customer Information -->
          <div class="info-section">
            <div class="columns">
              <div class="column">
                <div class="field">
                  <label class="label">First Name</label>
                  <p class="control">{{ comp.firstName }}</p>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">Last Name</label>
                  <p class="control">{{ comp.lastName }}</p>
                </div>
              </div>
              <div class="column">
                <div class="field">
                  <label class="label">Email</label>
                  <p class="control">{{ comp.email || "N/A" }}</p>
                </div>
              </div>
            </div>
            <div class="columns" v-if="comp.createdByName || comp.createdAt">
              <div class="column" v-if="comp.createdByName">
                <div class="field">
                  <label class="label">Created By</label>
                  <p class="control">{{ comp.createdByName }}</p>
                </div>
              </div>
              <div class="column" v-if="comp.createdAt">
                <div class="field">
                  <label class="label">Created At</label>
                  <p class="control">{{ formatTimestamp(comp.createdAt) }}</p>
                </div>
              </div>
            </div>
            <div class="columns" v-if="comp.discountCode">
              <div class="column">
                <div class="field">
                  <label class="label">Note</label>
                  <p class="control">{{ comp.discountCode }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Comp Summary -->
          <div class="section">
            <h4>Comp Summary</h4>
            <table class="table is-fullwidth line-items-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Event</th>
                  <th>Description</th>
                  <th>Quantity</th>
                  <th class="has-text-right">Price</th>
                  <th class="has-text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ formatDate(date) }}</td>
                  <td>{{ eventTitle }}</td>
                  <td>{{ comp.tierName || "Comp" }}</td>
                  <td>{{ comp.quantity }}</td>
                  <td class="has-text-right">$0.00</td>
                  <td class="has-text-right">$0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else class="has-text-centered">
          <p>No comp details available.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "CompDetailsModal",
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    comp: {
      type: Object,
      default: null
    },
    size: {
      type: String,
      default: "large",
      validator: value =>
        ["normal", "medium", "large", "extra-large"].includes(value)
    }
  },
  computed: {
    ...mapGetters({
      event: "event",
      dateSummary: "dateSummary"
    }),
    eventTitle() {
      return this.event?.title || "N/A";
    },
    date() {
      return this.dateSummary?.startDate || null;
    }
  },
  methods: {
    close() {
      this.$emit("close");
    },
    formatDate(dateStr) {
      if (!dateStr) return "N/A";
      // Check if it's a date string in YYYY-MM-DD format
      if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const [year, month, day] = dateStr.split("-");
        return new Date(year, month - 1, day).toLocaleDateString();
      }
      return new Date(dateStr).toLocaleDateString();
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return "N/A";
      if (timestamp.seconds) {
        // Firestore timestamp
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      return new Date(timestamp).toLocaleString();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.modal-content {
  background-color: white;
  border-radius: $radius;
  position: relative;
}

.modal-inner-wrapper {
  margin: 30px;
  margin-top: 0;
  margin-right: 0;
  max-height: 70vh;
  overflow-y: auto;
  padding: 30px;
  padding-top: 30px;
  padding-right: 30px;
}

.info-section {
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  background-color: $gray6;
  border-radius: $radius;

  .columns {
    margin-bottom: 0;
  }

  .columns:not(:last-child) {
    margin-bottom: 0.25rem;
  }
}

.section {
  margin-bottom: 0.75rem;
  padding: 0;
}

h1 {
  font-family: $family-primary;
  font-weight: bold;
  margin-bottom: 1rem;
}

h4 {
  font-family: $family-primary;
  font-weight: 600;
  color: $gray2;
  margin-bottom: 0.75rem;
  font-size: 1.1rem;
}

.label {
  color: $gray3;
  font-weight: 600;
  font-size: 0.8rem;
  margin-bottom: 0.1rem;
}

.control {
  font-family: $family-primary;
  font-size: 0.9rem;
}

.field {
  margin-bottom: 0.25rem;

  p {
    margin: 0.25rem 0;
  }
}

.line-items-table {
  margin-bottom: 1rem;
  width: 100%;
}

#modalclose {
  background: $gray1;
  font-size: 1.2em;
  height: 40px;
  width: 40px;
  color: white;
  text-align: center;
  line-height: 42px;
  border-top-right-radius: $radius;
  position: absolute;
  top: 0;
  right: 0;
  text-decoration: none;
}

#modalclose:hover {
  background: $gray2;
}
</style>
